import CustomerPriceMatrixPreviewMutation from '../../graphql/mutations/customer/CustomerPriceMatrixPreviewMutation.graphql';
import CustomerPriceMatrixActivateMutation from '../../graphql/mutations/customer/CustomerPriceMatrixActivateMutation.graphql';
import CustomerPriceMatrixDeactivateMutation from '../../graphql/mutations/customer/CustomerPriceMatrixDeactivateMutation.graphql';
import CustomerPriceMatricesActivateMutation from '../../graphql/mutations/customer/CustomerPriceMatricesActivateMutation.graphql';
import CustomerPriceMatricesDeactivateMutation from '../../graphql/mutations/customer/CustomerPriceMatricesDeactivateMutation.graphql';
import { InfoButton, SuccessButton } from '../../components/connectedForm/Button';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import getIn from '../../components/connectedForm/utils/getIn';
import getClientMutationId from '../../utils/getClientMutationId';
import { IWorkspaceAction } from '../../@types/workspaceTypes';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { useActionCriteria } from '../../utils/useActionProps';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { useCallback, useContext } from 'react';
import {
  AlertType,
  ICustomerPriceMatricesActivateMutationResult,
  ICustomerPriceMatricesActivateMutationVariables,
  ICustomerPriceMatricesDeactivateMutationResult,
  ICustomerPriceMatricesDeactivateMutationVariables,
} from '../../graphql/schema/p4types';
import { IDeprecatedFormAction } from '../../@types/entityTypes';
import WorkspaceContext from '../../components/Context/WorkspaceContext';

function queryVariablesFromProps(props) {
  return {
    variables: {
      input: {
        clientMutationId: getClientMutationId(),
        customerPriceMatrix: normalizeVariables(props.values || props.formData.data),
      },
    },
  };
}

export const formActions: Array<IDeprecatedFormAction<any>> = [
  {
    description: 'Preview',
    component: InfoButton,
    fn: queryVariablesFromProps,
    individualButton: true,
    performTemporarySave: true,
    mutation: CustomerPriceMatrixPreviewMutation,
    mutationWrapper: {
      onCompleteMutation: (data) => {
        let job = getIn(data, 'generateCustomerPriceMatrixPreview.result');
        const path = `/Sales/CustomerPricing/CustomerPriceMatrix/Preview/Workspace/${job.jobId}/${job.properties.uuid}`;
        window.open(window.location.origin + path, '_blank');
      },
    },
  },
  {
    description: 'Activate',
    component: SuccessButton,
    fn: queryVariablesFromProps,
    individualButton: false,
    performSave: true,
    mutation: CustomerPriceMatrixActivateMutation,
    mutationWrapper: {
      subjectSuccess: 'Price matrix activated',
      messageSuccess: 'Price matrix has been activated.',
    },
  },
  {
    description: 'Deactivate',
    component: SuccessButton,
    fn: queryVariablesFromProps,
    individualButton: false,
    performSave: true,
    mutation: CustomerPriceMatrixDeactivateMutation,
    mutationWrapper: {
      subjectSuccess: 'Price matrix deactivated',
      messageSuccess: 'Price matrix has been deactivated.',
    },
  },
];

export const preview = [
  {
    description: 'Activate',
    call: queryVariablesFromProps,
    mutation: CustomerPriceMatrixActivateMutation,
    mutationWrapper: {
      subjectSuccess: 'Price matrix activated',
      messageSuccess: 'Price matrix has been activated.',
    },
  },
  {
    description: 'Deactivate',
    call: queryVariablesFromProps,
    mutation: CustomerPriceMatrixDeactivateMutation,
    mutationWrapper: {
      subjectSuccess: 'Price matrix deactivated',
      messageSuccess: 'Price matrix has been deactivated.',
    },
  },
];

enum ICustomerPriceMatrixWorkspaceAction {
  Activate = 'CUSTOMER_PRICE_MATRICES_ACTIVATE',
  Deactivate = 'CUSTOMER_PRICE_MATRICES_DEACTIVATE',
}

export const workspaceActionsForPermissions: Array<IWorkspaceAction<ICustomerPriceMatrixWorkspaceAction>> = [
  { actionReference: ICustomerPriceMatrixWorkspaceAction.Activate },
  { actionReference: ICustomerPriceMatrixWorkspaceAction.Deactivate },
];

export const useCustomerPriceMatrixWorkspaceActions = (): [
  actions: Array<IWorkspaceAction<ICustomerPriceMatrixWorkspaceAction>>
] => {
  const { dispatchSimpleToast } = useP4ToastController();
  const actionCriteria = useActionCriteria();
  const { reloadData } = useContext(WorkspaceContext);

  const [activatePriceMatrices] = useBackgroundMutation<
    ICustomerPriceMatricesActivateMutationResult,
    ICustomerPriceMatricesActivateMutationVariables
  >(CustomerPriceMatricesActivateMutation, {
    onCompleteJob: () => {
      dispatchSimpleToast('Customer Price Matrix Activation', `Price matrices activated.`, AlertType.Success);
      reloadData?.();
    },
  });

  const onClickActivate = useCallback(() => {
    activatePriceMatrices({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          filters: actionCriteria.filters,
          customerPriceMatrices: actionCriteria.actionEntities,
          include: actionCriteria.include,
        },
      },
    });
  }, [actionCriteria]);

  const [deactivatePriceMatrices] = useBackgroundMutation<
    ICustomerPriceMatricesDeactivateMutationResult,
    ICustomerPriceMatricesDeactivateMutationVariables
  >(CustomerPriceMatricesDeactivateMutation, {
    onCompleteJob: () => {
      dispatchSimpleToast('Customer Price Matrix Deactivation', `Price matrices deactivated.`, AlertType.Success);
      reloadData?.();
    },
  });

  const onClickDeactivate = useCallback(() => {
    deactivatePriceMatrices({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          filters: actionCriteria.filters,
          customerPriceMatrices: actionCriteria.actionEntities,
          include: actionCriteria.include,
        },
      },
    });
  }, [actionCriteria]);

  return [
    [
      {
        key: 'activate',
        text: 'Activate',
        onClick: onClickActivate,
        actionReference: ICustomerPriceMatrixWorkspaceAction.Activate,
      },
      {
        key: 'deactivate',
        text: 'Deactivate',
        onClick: onClickDeactivate,
        actionReference: ICustomerPriceMatrixWorkspaceAction.Deactivate,
      },
    ],
  ];
};
